import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import {
  useListProjectTurns,
  useRestoreCheckpoint,
  useTurnState,
  useUndoTurn,
  type AiTurnSummary,
} from "@vorloop/api-client-react";
import { IconChevronDown, IconDatabase, IconShield } from "../layout/Icons";
import { InviteModal } from "./InviteModal";
import { PublishModal } from "./PublishModal";
import { SandboxControl } from "./SandboxControl";

export type AgentMode = "agent-2" | "plan" | "auto";
export type CenterTab = "preview" | "canvas" | "design" | "domains" | "audit" | "tools" | "database" | "secrets";
export type DeviceMode = "web" | "ios";

export interface TopBarProps {
  projectId: string;
  projectName: string;
  designName?: string;
  agentMode: AgentMode;
  onAgentModeChange: (m: AgentMode) => void;
  /** Tabs currently surfaced in the strip. First entry is the pinned Preview. */
  openCenterTabs: ReadonlyArray<CenterTab>;
  /** Active tab — the one rendered in the center pane. */
  centerTab: CenterTab;
  /** Activate an already-open tab (no-op if not in openCenterTabs). */
  onCenterTabChange: (t: CenterTab) => void;
  /** Close a non-pinned tab. */
  onCloseCenterTab: (t: CenterTab) => void;
  /** "+" button — open the launcher (which is the "tools" tab). */
  onAddCenterTab: () => void;
  deviceMode: DeviceMode;
  onDeviceModeChange: (d: DeviceMode) => void;
  onTryOnDevice?: () => void;
  onInvite?: () => void;
  onPublish?: () => void;
}

const TAB_META: Record<CenterTab, { label: string; icon?: React.ReactNode }> = {
  preview: { label: "Preview" },
  canvas: { label: "Canvas" },
  design: { label: "Design" },
  domains: { label: "Domains" },
  audit: { label: "Audit" },
  database: { label: "Database" },
  secrets: { label: "Secrets" },
  tools: { label: "New tab" },
};

const AGENT_LABEL: Record<AgentMode, string> = {
  "agent-2": "Agent",
  plan: "Plan",
  auto: "Auto",
};

export function TopBar(props: TopBarProps) {
  const [, setLocation] = useLocation();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  return (
    <>
    <header
      style={{
        height: 44,
        flexShrink: 0,
        borderBottom: "1px solid var(--border-default)",
        background: "var(--bg-panel)",
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        alignItems: "center",
        padding: "0 10px",
        gap: 8,
      }}
    >
      {/* LEFT — project name + agent picker */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <button
          type="button"
          onClick={() => setLocation("/")}
          aria-label="Back to projects"
          style={iconBtn}
        >
          <PlayCaretIcon />
        </button>
        <span
          title={props.projectName}
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "var(--text-primary)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 260,
          }}
        >
          {props.projectName}
        </span>
        <ActivityIndicator projectId={props.projectId} />
        <AgentPicker mode={props.agentMode} onChange={props.onAgentModeChange} />
      </div>

      {/* CENTER — Replit-style closable tab strip + "+" */}
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: 0,
          minWidth: 0,
          overflowX: "auto",
        }}
      >
        {props.openCenterTabs.map((t, i) => {
          const meta = TAB_META[t];
          const active = t === props.centerTab;
          const pinned = i === 0 && t === "preview";
          return (
            <div
              key={t}
              role="tab"
              aria-selected={active}
              onClick={() => props.onCenterTabChange(t)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                background: active ? "var(--bg-canvas)" : "transparent",
                color: active ? "var(--text-primary)" : "var(--text-tertiary)",
                borderRight: "1px solid var(--border-default)",
                borderTop: active ? "1px solid var(--accent-blue)" : "1px solid transparent",
                fontSize: 12,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              title={meta.label}
            >
              {t === "preview" && <DotIcon active={active} />}
              {t === "tools" && <ToolsIcon />}
              <span>{meta.label}</span>
              {!pinned && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onCloseCenterTab(t);
                  }}
                  aria-label={`Close ${meta.label}`}
                  style={{
                    width: 14,
                    height: 14,
                    border: "none",
                    background: "transparent",
                    color: "var(--text-tertiary)",
                    cursor: "pointer",
                    padding: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 3,
                  }}
                >
                  ×
                </button>
              )}
            </div>
          );
        })}
        <button
          type="button"
          onClick={props.onAddCenterTab}
          aria-label="Add tab"
          style={{
            padding: "6px 10px",
            background: "transparent",
            border: "none",
            color: "var(--text-tertiary)",
            cursor: "pointer",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          +
        </button>
      </div>

      {/* RIGHT — compact toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
        <SimulateToggle
          mode={props.deviceMode}
          onChange={props.onDeviceModeChange}
        />
        <button
          type="button"
          aria-label="Refresh"
          style={iconBtn}
          title="Refresh preview"
        >
          <RefreshIcon />
        </button>
        <HistoryMenu projectId={props.projectId} />
        <CheckpointsMenu projectId={props.projectId} />
        <SandboxControl projectId={props.projectId} />
        <button
          type="button"
          aria-label="Try on device"
          onClick={props.onTryOnDevice}
          style={iconBtn}
          title="Try on device"
        >
          📱
        </button>
        <button
          type="button"
          onClick={() => {
            setInviteOpen(true);
            props.onInvite?.();
          }}
          style={ghostBtn}
        >
          Invite
        </button>
        <button
          type="button"
          onClick={() => {
            setPublishOpen(true);
            props.onPublish?.();
          }}
          style={publishBtn}
        >
          <SparkleIcon />
          Publish
        </button>
      </div>
    </header>
    <InviteModal
      projectId={props.projectId}
      projectName={props.projectName}
      open={inviteOpen}
      onClose={() => setInviteOpen(false)}
    />
    <PublishModal
      projectId={props.projectId}
      projectName={props.projectName}
      open={publishOpen}
      onClose={() => setPublishOpen(false)}
    />
    </>
  );
}

function ActivityIndicator({ projectId }: { projectId: string }) {
  const state = useTurnState(projectId);
  if (!state.data?.running) return null;
  return (
    <span
      title="Agent is running a turn"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 8px",
        background: "rgba(37, 99, 235, 0.12)",
        color: "#60a5fa",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 7,
          height: 7,
          borderRadius: 4,
          background: "#60a5fa",
          display: "inline-block",
          animation: "vp-pulse 1.4s ease-in-out infinite",
        }}
      />
      Agent working
    </span>
  );
}

function HistoryMenu({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useCloseOnOutside(ref, () => setOpen(false));
  const turns = useListProjectTurns(projectId, { enabled: open });
  const undo = useUndoTurn(projectId);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        aria-label="History"
        onClick={() => setOpen((v) => !v)}
        style={ghostBtn}
      >
        History
      </button>
      {open && (
        <DropdownPanel align="right" width={400}>
          <DropdownHeader>Recent turns</DropdownHeader>
          {turns.isLoading && <DropdownEmpty>Loading…</DropdownEmpty>}
          {!turns.isLoading && (turns.data?.items.length ?? 0) === 0 && (
            <DropdownEmpty>No turns yet — send a prompt.</DropdownEmpty>
          )}
          {undo.error && (
            <div
              style={{
                margin: "0 12px 8px",
                padding: 8,
                fontSize: 11,
                color: "var(--accent-red)",
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.3)",
                borderRadius: 6,
              }}
            >
              {undo.error.message}
            </div>
          )}
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {turns.data?.items.map((t) => (
              <TurnRow
                key={t.id}
                turn={t}
                onUndo={
                  t.finishReason === "stop" && t.toolCallCount > 0
                    ? () => {
                        if (window.confirm("Undo this turn? Project will revert to its state before the turn ran.")) {
                          undo.mutate({ turnId: t.id });
                        }
                      }
                    : undefined
                }
                undoing={undo.isPending && undo.variables?.turnId === t.id}
              />
            ))}
          </div>
        </DropdownPanel>
      )}
    </div>
  );
}

function CheckpointsMenu({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useCloseOnOutside(ref, () => setOpen(false));
  const turns = useListProjectTurns(projectId, { enabled: open });
  const restore = useRestoreCheckpoint(projectId);
  const checkpoints =
    turns.data?.items.filter((t) => t.finishReason === "stop" && t.toolCallCount > 0) ?? [];

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        aria-label="Checkpoints"
        onClick={() => setOpen((v) => !v)}
        style={ghostBtn}
      >
        Checkpoints
      </button>
      {open && (
        <DropdownPanel align="right" width={360}>
          <DropdownHeader>Checkpoints</DropdownHeader>
          <DropdownSub>
            One snapshot per completed mutating turn. Restore overwrites files
            present in the snapshot; files you've added since are kept.
          </DropdownSub>
          {turns.isLoading && <DropdownEmpty>Loading…</DropdownEmpty>}
          {!turns.isLoading && checkpoints.length === 0 && (
            <DropdownEmpty>No checkpoints yet.</DropdownEmpty>
          )}
          {restore.error && (
            <div
              style={{
                margin: "0 12px 8px",
                padding: 8,
                fontSize: 11,
                color: "var(--accent-red)",
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.3)",
                borderRadius: 6,
              }}
            >
              {restore.error.message}
            </div>
          )}
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {checkpoints.map((t) => (
              <CheckpointRow
                key={t.id}
                turn={t}
                onRestore={() => {
                  if (window.confirm("Restore project to this checkpoint? Files written since will remain.")) {
                    restore.mutate({ turnId: t.id });
                  }
                }}
                pending={restore.isPending && restore.variables?.turnId === t.id}
              />
            ))}
          </div>
        </DropdownPanel>
      )}
    </div>
  );
}

function TurnRow({
  turn,
  onUndo,
  undoing,
}: {
  turn: AiTurnSummary;
  onUndo?: () => void;
  undoing?: boolean;
}) {
  const time = relativeTime(turn.createdAt);
  const isError = turn.finishReason === "error" || turn.errorMessage;
  const dotColor = isError
    ? "var(--accent-red)"
    : turn.finishReason === "stop"
      ? "var(--accent-green)"
      : "var(--accent-amber)";
  return (
    <div
      style={{
        padding: "8px 12px",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        borderTop: "1px solid var(--border-default)",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          marginTop: 5,
          flexShrink: 0,
          borderRadius: 4,
          background: dotColor,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            color: "var(--text-primary)",
            fontSize: 12,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={turn.prompt}
        >
          {turn.prompt}
        </div>
        <div
          style={{
            color: "var(--text-tertiary)",
            fontSize: 11,
            display: "flex",
            gap: 10,
            marginTop: 2,
          }}
        >
          <span>{turn.routeKey}</span>
          <span>
            {turn.toolCallCount} call{turn.toolCallCount === 1 ? "" : "s"}
          </span>
          <span>{Math.round(turn.durationMs / 1000)}s</span>
          <span style={{ marginLeft: "auto" }}>{time}</span>
        </div>
      </div>
      {onUndo && (
        <button
          type="button"
          onClick={onUndo}
          disabled={undoing}
          style={{
            padding: "4px 8px",
            background: undoing ? "var(--bg-active)" : "transparent",
            color: undoing ? "var(--text-tertiary)" : "var(--text-secondary)",
            border: "1px solid var(--border-default)",
            borderRadius: 6,
            fontSize: 11,
            cursor: undoing ? "wait" : "pointer",
            flexShrink: 0,
            alignSelf: "flex-start",
          }}
        >
          {undoing ? "Undoing…" : "Undo"}
        </button>
      )}
    </div>
  );
}

function CheckpointRow({
  turn,
  onRestore,
  pending,
}: {
  turn: AiTurnSummary;
  onRestore: () => void;
  pending: boolean;
}) {
  const time = relativeTime(turn.createdAt);
  const text = turn.assistantText.trim();
  const summary = text ? firstLine(text) : `${turn.toolCallCount} tool calls`;
  return (
    <div
      style={{
        padding: "10px 12px",
        borderTop: "1px solid var(--border-default)",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            color: "var(--text-primary)",
            fontSize: 12,
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={summary}
        >
          {summary}
        </div>
        <div style={{ color: "var(--text-tertiary)", fontSize: 11, marginTop: 2 }}>
          {turn.toolCallCount} mutations · {time}
        </div>
      </div>
      <button
        type="button"
        onClick={onRestore}
        disabled={pending}
        style={{
          padding: "4px 8px",
          background: pending ? "var(--bg-active)" : "transparent",
          color: pending ? "var(--text-tertiary)" : "var(--text-secondary)",
          border: "1px solid var(--border-default)",
          borderRadius: 6,
          fontSize: 11,
          cursor: pending ? "wait" : "pointer",
          flexShrink: 0,
        }}
      >
        {pending ? "Restoring…" : "Restore"}
      </button>
    </div>
  );
}

function DropdownPanel({
  children,
  align,
  width,
}: {
  children: React.ReactNode;
  align: "left" | "right";
  width: number;
}) {
  return (
    <div
      role="menu"
      style={{
        position: "absolute",
        top: "calc(100% + 4px)",
        [align]: 0,
        width,
        background: "var(--bg-panel)",
        border: "1px solid var(--border-strong)",
        borderRadius: 10,
        boxShadow: "0 18px 40px -10px rgba(0,0,0,0.6)",
        overflow: "hidden",
        zIndex: 20,
      }}
    >
      {children}
    </div>
  );
}

function DropdownHeader({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: "10px 12px",
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color: "var(--text-secondary)",
      }}
    >
      {children}
    </div>
  );
}

function DropdownSub({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: "0 12px 8px",
        fontSize: 11,
        color: "var(--text-tertiary)",
        lineHeight: 1.4,
      }}
    >
      {children}
    </div>
  );
}

function DropdownEmpty({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: 14, fontSize: 12, color: "var(--text-tertiary)" }}>
      {children}
    </div>
  );
}

function useCloseOnOutside(ref: React.RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && ref.current.contains(e.target)) return;
      onClose();
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onClose]);
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = (Date.now() - then) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function firstLine(s: string): string {
  const line = s.split("\n").find((l) => l.trim().length > 0) ?? "";
  return line.length > 80 ? `${line.slice(0, 79)}…` : line;
}

function AgentPicker({ mode, onChange }: { mode: AgentMode; onChange: (m: AgentMode) => void }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 4px 3px 8px",
        background: "var(--bg-panel-2)",
        border: "1px solid var(--border-default)",
        borderRadius: 8,
      }}
    >
      <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{AGENT_LABEL[mode]}</span>
      <select
        value={mode}
        onChange={(e) => onChange(e.target.value as AgentMode)}
        aria-label="Agent mode"
        style={{
          background: "transparent",
          color: "var(--text-secondary)",
          border: "none",
          fontSize: 12,
          appearance: "none",
          cursor: "pointer",
          padding: "0 14px 0 0",
        }}
      >
        <option value="agent-2">2</option>
        <option value="plan">Plan</option>
        <option value="auto">Auto</option>
      </select>
      <span
        style={{
          color: "var(--text-tertiary)",
          display: "inline-flex",
          marginLeft: -16,
          pointerEvents: "none",
        }}
      >
        <IconChevronDown size={12} />
      </span>
    </div>
  );
}

function CenterTabButton({
  label,
  icon,
  active,
  onClick,
  showCaret,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
  showCaret?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 10px",
        background: active ? "var(--bg-active)" : "transparent",
        color: active ? "var(--text-primary)" : "var(--text-secondary)",
        border: "none",
        borderRadius: 7,
        fontSize: 12,
        cursor: "pointer",
      }}
    >
      {icon}
      {label}
      {showCaret && <IconChevronDown size={10} />}
    </button>
  );
}

function SimulateToggle({
  mode,
  onChange,
}: {
  mode: DeviceMode;
  onChange: (m: DeviceMode) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Simulate on"
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: "var(--bg-panel-2)",
        border: "1px solid var(--border-default)",
        borderRadius: 8,
        padding: 2,
        fontSize: 12,
        gap: 2,
      }}
    >
      <SegBtn active={mode === "web"} onClick={() => onChange("web")}>
        Web
      </SegBtn>
      <SegBtn active={mode === "ios"} onClick={() => onChange("ios")}>
        iOS
      </SegBtn>
    </div>
  );
}

function SegBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: "4px 10px",
        background: active ? "var(--bg-active)" : "transparent",
        color: active ? "var(--text-primary)" : "var(--text-secondary)",
        border: "none",
        borderRadius: 6,
        fontSize: 12,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

const iconBtn: React.CSSProperties = {
  width: 26,
  height: 26,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  border: "none",
  color: "var(--text-secondary)",
  cursor: "pointer",
  borderRadius: 6,
};

const ghostBtn: React.CSSProperties = {
  padding: "5px 12px",
  background: "transparent",
  border: "1px solid var(--border-default)",
  color: "var(--text-secondary)",
  borderRadius: 8,
  fontSize: 12,
  cursor: "pointer",
};

const publishBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 14px",
  background: "linear-gradient(135deg,#8B5CF6,#6366F1)",
  border: "none",
  color: "#fff",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};

/* ---------- minor inline icons ---------- */
function PlayCaretIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="m8 5 11 7-11 7Z" />
    </svg>
  );
}

function DotIcon({ active }: { active: boolean }) {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10">
      <circle
        cx="5"
        cy="5"
        r={active ? 4 : 3}
        fill={active ? "var(--text-primary)" : "none"}
        stroke="currentColor"
        strokeWidth="1.4"
      />
    </svg>
  );
}

function CanvasIcon({ active }: { active: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="3" width="18" height="18" rx="2" fill={active ? "currentColor" : "none"} opacity={active ? 0.15 : 1} />
      <rect x="3" y="3" width="18" height="18" rx="2" />
    </svg>
  );
}

function AuditIcon({ active }: { active: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="3" width="16" height="18" rx="2" fill={active ? "currentColor" : "none"} opacity={active ? 0.15 : 1} />
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </svg>
  );
}

function GlobeIcon({ active }: { active: boolean }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" fill={active ? "currentColor" : "none"} opacity={active ? 0.15 : 1} />
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </svg>
  );
}

function ToolsIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M14 6h7M14 12h7M14 18h7" />
      <path d="M4 6h6v6H4zM4 14h6v6H4z" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </svg>
  );
}

function SparkleIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2 14 9 21 11 14 13 12 20 10 13 3 11 10 9Z" />
    </svg>
  );
}
